'use client';

import { useLocalStorage } from '@shared/hooks';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useCallback, type ComponentProps, type ReactNode } from 'react';
import { useLotteryNextDraw } from '../../../lotteries/hooks';
import { getLotteryDrawFrequency } from '../../../lotteries/utils';
import type { LotteryName } from '../../../lottery-purchases/types';
import { ResultsHeader, ResultsViewToggle } from '../../components';
import { useCurrentResult } from '../../hooks';
import { ResultsHeaderSkeleton } from '../../skeletons/ResultsHeaderSkeleton';
import { DEFAULT_AMOUNT_DRAWS_TO_SHOW, type DrawingsResponse, type ResultsConfig } from '../../types';
import { ResultsPageLayout } from '../ResultsPageLayout/ResultsPageLayout';

type ResultsLatestPageProps = ComponentProps<'div'> & {
  data: DrawingsResponse;
  lotteryName: LotteryName;
  amountDrawsToShow?: number;
  config?: ResultsConfig;
  backFallbackUrl?: string;
  pageHeaderActions?: ReactNode;
};

/**
 * ResultsLatestPage component displays the latest lottery results page.
 *
 * It renders the page header, results header with a date dropdown, next draw information,
 * and the prize tables for the selected drawing. The component manages the current result
 * selection using the useCurrentResult hook, allowing users to switch between available drawing dates.
 *
 * @param data - The array of drawing objects to display.
 * @param lotteryName - The name of the lottery.
 * @param amountDrawsToShow - How many drawings the date picker should list.
 * @param config - (Optional) configuration for the results page (e.g. hiding winners).
 * @param backFallbackUrl - Optional fallback URL for the header back button.
 * @param pageHeaderActions - Optional header actions (e.g. the Live Draw / Results toggle).
 */
function ResultsLatestPage({ data, lotteryName, amountDrawsToShow, config, backFallbackUrl, pageHeaderActions, children, ...props }: ResultsLatestPageProps) {
  const nextCloseLeadTime = config?.nextCloseLeadTime;
  const { data: nextDraws, isLoading: isNextDrawLoading } = useLotteryNextDraw(lotteryName);
  const nextDraw = nextDraws?.find(draw => draw.lotteryName === lotteryName);
  const { currentResult, drawingDates, handleResultChange } = useCurrentResult(data, getLotteryDrawFrequency(lotteryName), {
    lotteryName,
    nextDrawingDate: nextDraw?.drawingDate,
    nextClosingDate: nextDraw?.closingDate.regular,
    nextDrawingId: nextDraw?.id.regular,
    nextCloseLeadTime,
    maxDrawings: amountDrawsToShow ?? DEFAULT_AMOUNT_DRAWS_TO_SHOW,
  });
  const enableLiveDrawView = Boolean(config?.enableLiveDrawView);
  const t = useTranslations('lotteries.results.view_toggle');
  const isValidIndex = useCallback(
    (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < (enableLiveDrawView ? 2 : 1),
    [enableLiveDrawView]
  );
  const [selectedIndex, setSelectedIndex] = useLocalStorage(`lotteries.${lotteryName}.results.header-view`, 0, isValidIndex);
  const pathname = usePathname();
  const displayedResult = nextCloseLeadTime != null && isNextDrawLoading ? null : currentResult;

  const betUrl = pathname ? pathname.replace(/\/?[^/]+\/?$/, '') || '/' : '/';
  const isLiveDrawView = enableLiveDrawView && selectedIndex === 0;

  return (
    <ResultsPageLayout
      {...props}
      lotteryName={lotteryName}
      config={config}
      currentResult={displayedResult}
      drawings={data}
      betUrl={betUrl}
      backFallbackUrl={backFallbackUrl}
      pageHeaderActions={pageHeaderActions}
      actions={
        enableLiveDrawView ? (
          <ResultsViewToggle
            options={[
              { label: t('live_draw'), value: '0', 'data-testid': 'results-view-0', trackingProps: { 'data-track-name': 'Live Draw' } },
              { label: t('results'), value: '1', 'data-testid': 'results-view-1', trackingProps: { 'data-track-name': 'Results' } },
            ]}
            value={String(selectedIndex)}
            onChange={value => setSelectedIndex(Number(value))}
          />
        ) : undefined
      }
      header={
        displayedResult ? (
          <ResultsHeader
            lotteryName={lotteryName}
            drawingDates={drawingDates}
            currentResult={displayedResult}
            onDateChange={handleResultChange}
            enableLiveDrawView={enableLiveDrawView}
            isLiveDrawView={isLiveDrawView}
            data-track-name="Result"
            data-track-type="Latest"
          />
        ) : (
          <ResultsHeaderSkeleton lotteryName={lotteryName} isLiveDrawView={isLiveDrawView} />
        )
      }
      isLiveDrawView={isLiveDrawView}
    >
      {children}
    </ResultsPageLayout>
  );
}

export { ResultsLatestPage };
